// DTOs de C4 — programación de producción del cliente (Alpina).
//
// Modelo (mig 00005 + 00029):
//   - `programaciones` (header) con versionado: vigente_desde/vigente_hasta +
//     reemplazada_por_id. Sólo 1 versión vigente por (cliente, planta,
//     semana_iso). planta_id NULL = programa de todo el tenant (legacy/demo).
//   - `programaciones_detalle`: cada fila es (programacion_id, fecha, area_id,
//     sku_id, cantidad_paquetes, cantidad_kg). El programa se carga por ÁREA
//     (WS-4 / mig 00044); linea_id quedó opcional (legacy).
//   - `pedidos_extra_cliente`: tipo `evento_adicional` (suma al programa sin
//     versionar) o `nueva_version_programa` (genera nueva versión del programa).
//
// Endpoints C4:
//   GET    /api/coordinador/programaciones?semana_iso=YYYY-Www
//   GET    /api/coordinador/programaciones/:id (con detalles)
//   POST   /api/coordinador/programaciones (crea nueva versión)
//   GET    /api/coordinador/programaciones/diff?from=ID&to=ID
//   GET    /api/coordinador/pedidos-extra?semana_iso=YYYY-Www
//   POST   /api/coordinador/pedidos-extra
import { z } from 'zod';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
// =============================================================================
// Recursos para el form (clientes activos, líneas, SKUs)
// =============================================================================
const clienteResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    es_principal: z.boolean(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
});
// El programa de Alpina se carga por ÁREA/proceso (WS-4): la producción se mueve
// entre líneas de un área, así que clavar el programa a una línea era falsa
// precisión. La línea sigue disponible para C4-pedidos-extra (selector legacy).
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
});
const skuResumenSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    codigo_cliente: z.string(),
    descripcion: z.string(),
    formato: z.string().nullable(),
});
// =============================================================================
// Programación (header) — sin detalles
// =============================================================================
export const programacionResumenSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    /** Planta de la programación. NULL solo en programas legacy multi-planta. */
    planta_id: z.string().uuid().nullable(),
    planta_nombre: z.string().nullable(),
    semana_iso: z.string().regex(semanaIsoRegex),
    version: z.number().int().min(1),
    vigente_desde: z.string(), // ISO timestamp
    vigente_hasta: z.string().nullable(),
    reemplazada_por_id: z.string().uuid().nullable(),
    cargada_por_nombre: z.string(),
    cargada_por_apellido: z.string(),
    origen: z.enum(['excel_alpina', 'manual']),
    /** Total de paquetes en esta versión (suma de detalles). */
    total_paquetes: z.number(),
    /** Conteo de filas de detalle. */
    total_lineas_detalle: z.number().int().min(0),
    created_at: z.string(),
});
// =============================================================================
// Detalle de una programación
// =============================================================================
export const programacionDetalleSchema = z.object({
    fecha: z.string().date(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    area_nombre: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    cantidad_paquetes: z.number().min(0),
    cantidad_kg: z.number().nullable(),
});
export const programacionConDetallesSchema = programacionResumenSchema.extend({
    detalles: z.array(programacionDetalleSchema),
});
// =============================================================================
// Response GET /api/coordinador/programaciones?semana_iso=...
// =============================================================================
export const coordinadorProgramacionProduccionSemanaSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    }),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Lunes a domingo en YYYY-MM-DD. */
    dias: z.array(z.string().date()).length(7),
    fecha_hoy: z.string().date(),
    /** Recursos para el form de creación. */
    clientes: z.array(clienteResumenSchema),
    /** Líneas del scope (las usa el selector legacy de pedidos-extra). */
    lineas: z.array(lineaResumenSchema),
    /** Áreas del scope. El programa se carga por área (WS-4). */
    areas: z.array(areaResumenSchema),
    skus: z.array(skuResumenSchema),
    /** Mapa SKU→línea (skus_lineas) en plantas del scope. Legacy: lo conserva el
     * selector de pedidos-extra. */
    skus_lineas: z.array(z.object({ sku_id: z.string().uuid(), linea_id: z.string().uuid() })),
    /** Mapa SKU→área (derivado de skus_lineas → lineas.area_id) en plantas del
     * scope. El importador deriva el área del SKU: si es única la asigna; si hay
     * varias o ninguna, el coordinador la elige en el preview. */
    skus_areas: z.array(z.object({ sku_id: z.string().uuid(), area_id: z.string().uuid() })),
    /** Todas las versiones de programaciones de esa semana (todos los clientes). */
    programaciones: z.array(programacionResumenSchema),
});
// =============================================================================
// POST input — crear nueva programación
// =============================================================================
const detalleCreateSchema = z.object({
    fecha: z.string().date(),
    area_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    cantidad_paquetes: z.number().positive(),
    cantidad_kg: z.number().nonnegative().nullable().optional(),
});
export const programacionCreateSchema = z.object({
    cliente_id: z.string().uuid(),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Origen de la carga: manual (form) o excel_alpina (importador). Default manual. */
    origen: z.enum(['manual', 'excel_alpina']).default('manual'),
    detalles: z.array(detalleCreateSchema).min(1, 'Mínimo 1 fila de detalle'),
});
// =============================================================================
// Response GET /api/coordinador/programaciones/diff?from=X&to=Y
// =============================================================================
const detalleClaveSchema = z.object({
    fecha: z.string().date(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
});
export const programacionDiffSchema = z.object({
    /** Versión "antes". */
    from: programacionResumenSchema,
    /** Versión "después". */
    to: programacionResumenSchema,
    /** Detalles en `to` que NO estaban en `from`. */
    agregados: z.array(detalleClaveSchema.extend({
        cantidad_paquetes_nueva: z.number(),
    })),
    /** Detalles en `from` que ya NO están en `to`. */
    eliminados: z.array(detalleClaveSchema.extend({
        cantidad_paquetes_anterior: z.number(),
    })),
    /** Detalles en ambas versiones pero con cantidad distinta. */
    cambiados: z.array(detalleClaveSchema.extend({
        cantidad_paquetes_anterior: z.number(),
        cantidad_paquetes_nueva: z.number(),
        delta: z.number(),
    })),
});
// =============================================================================
// Pedidos extra
// =============================================================================
export const pedidoExtraTipoSchema = z.enum(['evento_adicional', 'nueva_version_programa']);
export const pedidoExtraSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    fecha_solicitud: z.string(),
    fecha_para: z.string().date(),
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    cantidad_solicitada_paquetes: z.number().positive(),
    cantidad_solicitada_kg: z.number().nullable(),
    tipo: pedidoExtraTipoSchema,
    programacion_relacionada_id: z.string().uuid().nullable(),
    registrado_por_nombre: z.string(),
    observaciones: z.string().nullable(),
    created_at: z.string(),
});
export const pedidoExtraCreateSchema = z.object({
    cliente_id: z.string().uuid(),
    fecha_para: z.string().date(),
    linea_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    cantidad_solicitada_paquetes: z.number().positive(),
    cantidad_solicitada_kg: z.number().nonnegative().nullable().optional(),
    tipo: pedidoExtraTipoSchema,
    observaciones: z.string().nullable().optional(),
});
export const pedidosExtraListSchema = z.object({
    semana_iso: z.string().regex(semanaIsoRegex),
    pedidos: z.array(pedidoExtraSchema),
});
//# sourceMappingURL=coordinador_programacion_produccion.js.map